/**
 * Runtime configuration utility.
 *
 * Replaces Next.js publicRuntimeConfig and serverRuntimeConfig which were removed in Next.js 16.
 * Server-side: reads directly from process.env.
 * Client-side: reads from window.__RUNTIME_CONFIG__ injected by _document.
 */

export interface RuntimeConfig {
  // Server-only (from serverRuntimeConfig)
  amplitudeApiKey?: string;
  hereKey?: string;
  rapidApiKey?: string;
  hereAutocompleteSearchUrl?: string;
  hereGeocoderLsUrl?: string;
  hereGeocodeSearchUrl?: string;
  hereRevgeocodeSearchUrl?: string;
  businessRegistry?: string;
  launchDarklySDKKey?: string;
  extoleEventsUrl?: string;
  extoleToken?: string;
  procoreOAuthClientId?: string;
  procoreOAuthClientSecret?: string;
  procoreOAuthRedirectUri?: string;
  oAuthStateSigningSecret?: string;
  // Public (from publicRuntimeConfig)
  otelExporterOtlpEndpoint?: string;
  isProd?: boolean;
  mapsKey?: string;
  selfUrl?: string;
  originUrl?: string;
  basePath?: string;
  assetsStaticBasePath?: string;
  appVersion?: string;
  bugsnagApiKey?: string;
  bugsnagEnvironment?: string;
  launchDarklyClientSideID?: string;
  loginLink?: string;
  procoreApiDomain?: string;
  mockUserLoggedIn?: boolean;
  mockUserPersona?: boolean;
  mockCommunicationStatus?: boolean;
  procoreAppBaseUrl?: string;
  pactsafeAccessId?: string;
  pactsafeGroupKey?: string;
  claimLink?: string;
  signupLink?: string;
}

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig;
  }
}

const defaultFeUrl = 'http://localhost:4070';
const defaultProcoreAppBaseUrl = 'https://app.procore.com';

/**
 * Builds public config by reading only safe env vars. Never touches server-only
 * secrets (procoreOAuthClientSecret, oAuthStateSigningSecret, hereKey, etc.).
 * Use this for client injection - secrets are never in this flow.
 */
export function getPublicRuntimeConfig(): RuntimeConfig {
  const basePath = process.env.BASE_PATH || '';
  const feUrl = process.env.NEXT_PUBLIC_FE_URL || defaultFeUrl;
  const procoreAppBaseUrl =
    process.env.PROCORE_APP_BASE_URL || defaultProcoreAppBaseUrl;

  return {
    amplitudeApiKey: process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY,
    otelExporterOtlpEndpoint:
      process.env.NEXT_PUBLIC_OTEL_EXPORTER_OTLP_ENDPOINT,
    isProd: process.env.STAGE === 'production',
    mapsKey: process.env.NEXT_PUBLIC_MAPS_KEY,
    selfUrl: feUrl,
    originUrl: process.env.NEXT_PUBLIC_ORIGIN_URL || defaultFeUrl,
    basePath,
    assetsStaticBasePath: basePath
      ? `${basePath}/assets/static`
      : '/assets/static',
    appVersion: process.env.APP_VERSION,
    bugsnagApiKey:
      process.env.BUGSNAG_API_KEY || '2ffe0d29f0f7d9472424fc47b98f565c',
    bugsnagEnvironment: process.env.STAGE || 'development',
    launchDarklyClientSideID: process.env.LAUNCH_DARKLY_CLIENT_SIDE_ID,
    loginLink: process.env.LOGIN_LINK,
    procoreApiDomain: process.env.PROCORE_API_DOMAIN,
    mockUserLoggedIn: process.env.NEXT_PUBLIC_MOCK_USER_LOGGED_IN === 'true',
    mockUserPersona: process.env.NEXT_PUBLIC_MOCK_USER_PERSONA === 'true',
    mockCommunicationStatus:
      process.env.NEXT_PUBLIC_MOCK_COMMUNICATION_STATUS === 'true',
    procoreAppBaseUrl,
    pactsafeAccessId: process.env.PACTSAFE_ACCESS_ID,
    pactsafeGroupKey: process.env.PACTSAFE_GROUP_KEY,
    claimLink: `${procoreAppBaseUrl}/webclients/signup/welcome?flow=claiming&business_id=`,
    signupLink: `${procoreAppBaseUrl}/webclients/signup/welcome?flow=default`,
  };
}

export function getServerRuntimeConfig(): RuntimeConfig {
  const basePath = process.env.BASE_PATH || '';
  const feUrl = process.env.NEXT_PUBLIC_FE_URL || defaultFeUrl;
  const procoreAppBaseUrl =
    process.env.PROCORE_APP_BASE_URL || defaultProcoreAppBaseUrl;

  return {
    amplitudeApiKey: process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY,
    hereKey: process.env.SECRET_HERE_API_KEY,
    rapidApiKey: process.env.RAPID_API_KEY,
    hereAutocompleteSearchUrl:
      process.env.HERE_AUTOCOMPLETE_SEARCH_URL ||
      'https://autocomplete.search.hereapi.com',
    hereGeocoderLsUrl:
      process.env.HERE_GEOCODER_LS_URL || 'https://geocoder.ls.hereapi.com',
    hereGeocodeSearchUrl:
      process.env.HERE_GEOCODE_SEARCH_URL ||
      'https://geocode.search.hereapi.com',
    hereRevgeocodeSearchUrl:
      process.env.HERE_REVGEOCODE_SEARCH_URL ||
      'https://revgeocode.search.hereapi.com',
    businessRegistry:
      process.env.BUSINESS_REGISTRY_URL || 'http://localhost:3010',
    launchDarklySDKKey: process.env.LAUNCH_DARKLY_SDK_KEY,
    extoleEventsUrl:
      process.env.EXTOLE_EVENTS_URL || 'https://api.extole.io/v5/events',
    extoleToken: process.env.EXTOLE_TOKEN,
    procoreOAuthClientId: process.env.PROCORE_OAUTH_CLIENT_ID,
    procoreOAuthClientSecret: process.env.PROCORE_OAUTH_CLIENT_SECRET,
    procoreOAuthRedirectUri: `${feUrl}/api/oauth/callback`,
    oAuthStateSigningSecret: process.env.OAUTH_STATE_SIGNING_SECRET,
    otelExporterOtlpEndpoint:
      process.env.NEXT_PUBLIC_OTEL_EXPORTER_OTLP_ENDPOINT,
    isProd: process.env.STAGE === 'production',
    mapsKey: process.env.NEXT_PUBLIC_MAPS_KEY,
    selfUrl: feUrl,
    originUrl: process.env.NEXT_PUBLIC_ORIGIN_URL || defaultFeUrl,
    basePath,
    assetsStaticBasePath: basePath
      ? `${basePath}/assets/static`
      : '/assets/static',
    appVersion: process.env.APP_VERSION,
    bugsnagApiKey:
      process.env.BUGSNAG_API_KEY || '2ffe0d29f0f7d9472424fc47b98f565c',
    bugsnagEnvironment: process.env.STAGE || 'development',
    launchDarklyClientSideID: process.env.LAUNCH_DARKLY_CLIENT_SIDE_ID,
    loginLink: process.env.LOGIN_LINK,
    procoreApiDomain: process.env.PROCORE_API_DOMAIN,
    mockUserLoggedIn: process.env.NEXT_PUBLIC_MOCK_USER_LOGGED_IN === 'true',
    mockUserPersona: process.env.NEXT_PUBLIC_MOCK_USER_PERSONA === 'true',
    mockCommunicationStatus:
      process.env.NEXT_PUBLIC_MOCK_COMMUNICATION_STATUS === 'true',
    procoreAppBaseUrl,
    pactsafeAccessId: process.env.PACTSAFE_ACCESS_ID,
    pactsafeGroupKey: process.env.PACTSAFE_GROUP_KEY,
    claimLink: `${procoreAppBaseUrl}/webclients/signup/welcome?flow=claiming&business_id=`,
    signupLink: `${procoreAppBaseUrl}/webclients/signup/welcome?flow=default`,
  };
}

export function getRuntimeConfig(): RuntimeConfig {
  if (typeof window === 'undefined') return getServerRuntimeConfig();
  return window.__RUNTIME_CONFIG__ || {};
}
